import api from './api';

export interface User {
  id: string;
  username: string;
  email: string;
  name: string;
  role: 'admin' | 'operator' | 'viewer';
  status: 'active' | 'inactive';
  last_login?: string;
  spop_id?: string;
  spop?: {
    id: string;
    name: string;
    areaCode: string;
    address?: string;
  };
  permissions?: Record<string, any>;
  createdAt?: string;
  updatedAt?: string;
}

export interface CreateUserRequest {
  username: string;
  email: string;
  password: string;
  name: string;
  role: 'admin' | 'operator' | 'viewer';
  status?: 'active' | 'inactive';
  spop_id?: string;
  permissions?: Record<string, any>;
}

export interface UpdateUserRequest {
  username?: string;
  email?: string;
  password?: string;
  name?: string;
  role?: 'admin' | 'operator' | 'viewer';
  status?: 'active' | 'inactive';
  spop_id?: string;
  permissions?: Record<string, any>;
}

export interface ChangePasswordRequest {
  currentPassword?: string;
  newPassword: string;
}

export interface UsersResponse {
  users: User[];
  pagination: {
    total: number;
    page: number;
    limit: number;
    totalPages: number;
  };
}

export interface UserStatsResponse {
  activeUsers: number;
  inactiveUsers: number;
  admins: number;
  operators: number;
  viewers: number;
  assignedToSpop: number;
}

export interface GetUsersParams {
  page?: number;
  limit?: number;
  search?: string;
  role?: string;
  status?: string;
  spop_id?: string;
}

class UserService {
  async getUsers(params: GetUsersParams = {}): Promise<UsersResponse> {
    const response = await api.get('/users', { params });
    return response.data.data;
  }

  async getUserById(id: string): Promise<User> {
    const response = await api.get(`/users/${id}`);
    return response.data.data;
  }

  async createUser(userData: CreateUserRequest): Promise<User> {
    const response = await api.post('/users', userData);
    return response.data.data;
  }

  async updateUser(id: string, userData: UpdateUserRequest): Promise<User> {
    const response = await api.put(`/users/${id}`, userData);
    return response.data.data;
  }

  async deleteUser(id: string): Promise<void> {
    await api.delete(`/users/${id}`);
  }

  async changePassword(id: string, passwordData: ChangePasswordRequest): Promise<void> {
    await api.patch(`/users/${id}/change-password`, passwordData);
  }

  async getUserStats(): Promise<UserStatsResponse> {
    const response = await api.get('/users/stats');
    return response.data.data;
  }

  async getOperatorsBySpop(spopId: string): Promise<{ spop: any; operators: User[] }> {
    const response = await api.get(`/users/spop/${spopId}/operators`);
    return response.data.data;
  }
}

export default new UserService();
